import * as React from 'react';
import { memo, useCallback } from 'react';

import { PopupNames } from 'core';
import {
  useLmsAccessBlock,
  usePopupData,
  usePopupStateOperations,
} from 'core/hooks';

import { LmsBlockModal } from '../LmsBlockModal';

/** popupData shape for the palette-dispatched LMS block/unblock popup. */
export interface ILmsBlockPopupData {
  studentId: string;
  mode: 'block' | 'unblock';
}

/**
 * Registry wrapper that lets the command palette open the existing
 * `LmsBlockModal` via `showPopup(PopupNames.lmsBlockPopup, { studentId, mode })`.
 * Owns the `useLmsAccessBlock` hook the student card would otherwise own, so the
 * palette never has to reach into a page component.
 */
export const LmsBlockPopup = memo(() => {
  const data = usePopupData(PopupNames.lmsBlockPopup);
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const lms = useLmsAccessBlock(data?.studentId);

  const close = useCallback(() => hidePopup(PopupNames.lmsBlockPopup), [
    hidePopup,
  ]);

  const onSubmit = useCallback(
    async (reason?: string) => {
      if (!data) return;
      const ok =
        data.mode === 'unblock'
          ? await lms.unblock()
          : await lms.block(reason || undefined);
      if (ok) close();
    },
    [data, lms, close],
  );

  if (!data) return null;

  return (
    <LmsBlockModal
      mode={data.mode}
      loading={lms.loading}
      onSubmit={onSubmit}
      onCancel={close}
    />
  );
});
